/** @jsx jsx */
import { css, jsx, useTheme } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';
import Media from 'react-media';

import { groupBy, keyBy, map, mapValues } from 'lodash';

import { useLocation } from 'js/lib/useRouter';

import { Grid, breakpoints } from '@coursera/cds-core';
import type { GridProps, ProductCardProps, Theme } from '@coursera/cds-core';

import EnterpriseSearchCards from 'bundles/enterprise-learner-search/components/search/EnterpriseSearchCards';
import { NUMBER_OF_RESULTS_PER_PAGE, SEARCH_FILTERS } from 'bundles/enterprise-learner-search/constants';
import { useUserAgent } from 'bundles/page/contexts/UserAgentApiContext';
import ActiveFilterItems from 'bundles/search-common/components/ActiveFilterItems';
import NoSearchResultsMessage from 'bundles/search-common/components/NoSearchResultsMessage';
import SearchResultsHeader from 'bundles/search-common/components/SearchResultsHeader';
import SearchResultsPagination from 'bundles/search-common/components/SearchResultsPagination';
import SortByIndex from 'bundles/search-common/components/SortByIndex';
import SearchFilters from 'bundles/search-common/components/filters/SearchFilters';
import { DEFAULT_SEARCH_FILTERS, SEARCH_FILTERS_INDEX_ID, SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { IS_PART_OF_COURSERA_PLUS } from 'bundles/search-common/constants/filter';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { SearchProductHit } from 'bundles/search-common/providers/searchTypes';
import { inferQueryFromParams } from 'bundles/search-common/utils/eventingUtils';
import { createFilterKeyToOptionsMap } from 'bundles/search-common/utils/utils';
import useTrackQueryResults from 'bundles/search-v2/hooks/useTrackQueryResults';

const styles = {
  header: (_: Theme, isMobile: boolean) => css`
    display: flex;
    justify-content: space-between;
    margin-bottom: ${isMobile ? '0px' : '12px'};

    @media (max-width: ${breakpoints.values.md - 1}px) {
      min-height: 32px;
    }
  `,
  sortBy: css`
    margin-top: 4px;
  `,
};

type Props = {
  programIds: string[];
  thirdPartyOrganizationId: string;
  searchCardsGridSpacing?: GridProps['spacing'];
  variant?: ProductCardProps['variant'];
  renderAdminContent?: (hit: SearchProductHit) => ProductCardProps['adminContent'];
  hiddenFilters?: string[];
  onSearchCardClick?: (hit: SearchProductHit) => void;
  noSearchResultsMessage?: JSX.Element;
  headerWithSearchResults?: JSX.Element;
  hitFilter?: (hit?: SearchProductHit) => boolean;
  isEnterpriseSERP?: boolean;
};

const EnterpriseSearch = ({
  programIds,
  thirdPartyOrganizationId,
  searchCardsGridSpacing,
  variant,
  renderAdminContent,
  hiddenFilters,
  onSearchCardClick,
  noSearchResultsMessage,
  headerWithSearchResults,
  hitFilter,
  isEnterpriseSERP,
}: Props) => {
  const theme = useTheme();
  const { query: queryParams } = useLocation();
  const userAgent = useUserAgent();
  const searchQuery = inferQueryFromParams(queryParams.query);

  const {
    getSearchResults,
    setPage,
    addFilters,
    removeFilters,
    clearFilters,
    setFiltersByFacet,
    isLoading,
    setSortBy,
  } = useSearch();
  const searchData = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const filterData = getSearchResults(SEARCH_FILTERS_INDEX_ID);
  const { elements, query, facets, pagination, totalPages, facetFilters, source, sortBy } = searchData || {};
  const allFilters = filterData?.facets;
  const allHits = elements as SearchProductHit[];
  const hits = hitFilter ? allHits?.filter(hitFilter) : allHits;
  const filterAttributes = [...DEFAULT_SEARCH_FILTERS, IS_PART_OF_COURSERA_PLUS];
  const [currentQuery, setCurrentQuery] = useState(query);
  const activeFilters = [...new Set(facetFilters?.flat())];
  const activeFiltersToShow = activeFilters.filter((f) => !f.includes(':-') && !hiddenFilters?.includes(f)); // hidden filters shouldn't be shown
  const filters = createFilterKeyToOptionsMap(facets, facetFilters, filterAttributes, allFilters);
  const groupedFilters = groupBy(activeFilters, (filter) => filter.split(':')[0]);
  const keyedFilters = keyBy(groupedFilters, (filter) => filter[0]?.split(':')?.[0]);
  const filtersApplied = mapValues(keyedFilters, (group) => map(group, (filter) => filter.split(':')[1]));
  const totalHits = pagination?.totalElements;
  const hasNoResults = totalHits === 0;

  const eventingData = {
    searchIndex: source?.indexName || '',
    searchIndexPosition: 1,
    page: pagination?.cursor || undefined,
    searchQuery,
    filtersApplied,
  };

  useTrackQueryResults({
    query: searchQuery,
    totalHits: totalHits || undefined,
  });

  useEffect(() => {
    if (currentQuery !== query && query !== undefined) {
      setCurrentQuery(query);
    }
  }, [currentQuery, query]);

  if (hasNoResults && noSearchResultsMessage) {
    return noSearchResultsMessage;
  }

  return (
    <Media query={{ maxWidth: breakpoints.values.md - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
      {(isMobile) => (
        <Grid container spacing={24}>
          {!hasNoResults && headerWithSearchResults}
          <Grid item xs={12} md={3}>
            <SearchResultsHeader
              searchQuery={searchQuery}
              numberOfHits={totalHits || undefined}
              shouldHide={!isMobile || isLoading}
              activeFilters={activeFiltersToShow}
              isLoading={!!isLoading}
              lessenHeaderFocus={true}
              hideHits={true}
            />
            <div css={styles.header(theme, isMobile)}>
              <div css={styles.sortBy}>
                <SearchFilters
                  filters={filters}
                  activeFilters={activeFilters}
                  eventingData={eventingData}
                  isMobile={isMobile}
                  showingNoResults={!!hasNoResults}
                  filtersListOrder={SEARCH_FILTERS}
                  setFiltersByFacet={(facetName: string, newFilters: string[]) => {
                    setFiltersByFacet(SEARCH_MAIN_INDEX_ID, facetName, newFilters);
                  }}
                  addFilters={(filter: string) => addFilters(SEARCH_MAIN_INDEX_ID, [filter])}
                  removeFilters={(filter: string) => removeFilters(SEARCH_MAIN_INDEX_ID, [filter])}
                  clearFilters={() => clearFilters(SEARCH_MAIN_INDEX_ID)}
                />
              </div>
              {!hasNoResults && isEnterpriseSERP && (
                <div>
                  <SortByIndex
                    setSortBy={(sortByValue) => setSortBy(SEARCH_MAIN_INDEX_ID, sortByValue)}
                    shouldHide={!isMobile}
                    sortByValue={sortBy}
                  />
                </div>
              )}
            </div>
          </Grid>
          <Grid item xs={12} md={9}>
            <div css={!isMobile ? styles.header(theme, isMobile) : null}>
              <div aria-live="polite" aria-atomic="true" css={styles.sortBy}>
                <SearchResultsHeader
                  searchQuery={searchQuery}
                  numberOfHits={totalHits || undefined}
                  shouldHide={isMobile || isLoading}
                  activeFilters={activeFiltersToShow}
                  isLoading={!!isLoading}
                  lessenHeaderFocus={true}
                  hideHits={true}
                />
              </div>
              {isEnterpriseSERP && (
                <div>
                  <SortByIndex
                    setSortBy={(sortByValue) => setSortBy(SEARCH_MAIN_INDEX_ID, sortByValue)}
                    shouldHide={isMobile}
                    sortByValue={sortBy}
                  />
                </div>
              )}
            </div>
            {!isMobile && activeFilters && !hasNoResults && (
              <ActiveFilterItems
                items={activeFiltersToShow}
                removeFilter={(filter: string) => removeFilters(SEARCH_MAIN_INDEX_ID, [filter])}
                clearFilters={() => clearFilters(SEARCH_MAIN_INDEX_ID)}
                eventingData={eventingData}
                isLoading={isLoading}
              />
            )}
            {hasNoResults ? (
              <NoSearchResultsMessage query={searchQuery} />
            ) : (
              <React.Fragment>
                <EnterpriseSearchCards
                  hits={hits}
                  resultsPerPage={NUMBER_OF_RESULTS_PER_PAGE}
                  eventingData={eventingData}
                  programId={programIds.length > 1 ? undefined : programIds[0]}
                  thirdPartyOrganizationId={thirdPartyOrganizationId}
                  gridSpacing={searchCardsGridSpacing}
                  isLoading={isLoading}
                  variant={variant}
                  renderAdminContent={renderAdminContent}
                  onSearchCardClick={onSearchCardClick}
                />
                <SearchResultsPagination
                  // note: edge case when on last page, cursor will be null because it is actually nextCursor value
                  // we need to fallback to totalPages
                  paginationData={{ ...pagination, totalPages, page: Number(pagination?.cursor) || totalPages || 1 }}
                  currentQuery={query}
                  setPage={(pageNum: number) => setPage(SEARCH_MAIN_INDEX_ID, pageNum)}
                  eventingData={eventingData}
                />
              </React.Fragment>
            )}
          </Grid>
        </Grid>
      )}
    </Media>
  );
};

export default EnterpriseSearch;
